import { useState, useEffect } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import RecordMetadata from '@/components/RecordMetadata';
import { useQuery } from '@tanstack/react-query';
import { priceBookBaseSchema, type AuditProfile } from '@prolarus/shared';
import { z } from 'zod';
import { useActiveStatusId } from '@/hooks/useDefaultStatus';
import { useAuth } from '@/contexts/AuthContext';
import { vendorService, regionService, segmentService, profileService } from '@/services';

// Form schema type from the base schema (without refinement for react-hook-form)
type PriceBookFormValues = z.infer<typeof priceBookBaseSchema>;

export interface PriceBookFormData {
  id?: string;
  name: string;
  vendor_id: string;
  region_id: string | null;
  segment_id: string | null;
  effective_date: string;
  expiry_date: string | null;
  created?: string | null;
  modified?: string | null;
  created_by_profile?: AuditProfile | null;
  updated_by_profile?: AuditProfile | null;
}

interface PriceBookFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  priceBook?: PriceBookFormData | null;
  onSave: (data: PriceBookFormData) => Promise<string | void>;
  saveButtonText?: string;
  onSecondarySave?: (data: PriceBookFormData) => Promise<string | void>;
  secondarySaveButtonText?: string;
  onDelete?: (id: string) => Promise<void>;
}

const defaultValues: PriceBookFormValues = {
  name: '',
  vendor_id: '',
  region_id: null,
  segment_id: null,
  effective_date: '',
  expiry_date: null,
};

export default function PriceBookFormDialog({
  open,
  onOpenChange,
  priceBook,
  onSave,
  saveButtonText,
  onSecondarySave,
  secondarySaveButtonText,
  onDelete,
}: PriceBookFormDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { user } = useAuth();

  const form = useForm<PriceBookFormValues>({
    resolver: zodResolver(priceBookBaseSchema),
    defaultValues,
  });

  // Fetch user profile for company_id
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const result = await profileService.getCurrentProfile();
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  // Get active status ID for vendor filtering
  const { data: activeVendorStatus } = useActiveStatusId('vendor');

  // Fetch vendors using service (filter by active status_id)
  const { data: vendors = [] } = useQuery({
    queryKey: ['vendors-active', profile?.company_id, activeVendorStatus?.id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const result = await vendorService.listActive(profile.company_id, activeVendorStatus?.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: open && !!profile?.company_id,
  });

  // Fetch regions using service
  const { data: regions = [] } = useQuery({
    queryKey: ['list-regions-active', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const result = await regionService.listActive(profile.company_id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: open && !!profile?.company_id,
  });

  // Fetch segments using service
  const { data: segments = [] } = useQuery({
    queryKey: ['list-segments-active', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const result = await segmentService.listActive(profile.company_id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: open && !!profile?.company_id,
  });

  useEffect(() => {
    if (open) {
      if (priceBook) {
        form.reset({
          name: priceBook.name || '',
          vendor_id: priceBook.vendor_id || '',
          region_id: priceBook.region_id || null,
          segment_id: priceBook.segment_id || null,
          effective_date: priceBook.effective_date || '',
          expiry_date: priceBook.expiry_date || null,
        });
      } else {
        form.reset(defaultValues);
      }
    }
  }, [open, priceBook, form]);

  const handleSubmit = async (data: PriceBookFormValues) => {
    setIsLoading(true);
    try {
      const formData: PriceBookFormData = {
        id: priceBook?.id,
        name: data.name,
        vendor_id: data.vendor_id,
        region_id: data.region_id || null,
        segment_id: data.segment_id || null,
        effective_date: data.effective_date,
        expiry_date: data.expiry_date || null,
        created: priceBook?.created,
        modified: priceBook?.modified,
        created_by: priceBook?.created_by,
        updated_by: priceBook?.updated_by,
      };
      await onSave(formData);
      onOpenChange(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSecondarySave = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    setIsLoading(true);
    try {
      const data = form.getValues();
      const formData: PriceBookFormData = {
        id: priceBook?.id,
        name: data.name,
        vendor_id: data.vendor_id,
        region_id: data.region_id || null,
        segment_id: data.segment_id || null,
        effective_date: data.effective_date,
        expiry_date: data.expiry_date || null,
        created: priceBook?.created,
        modified: priceBook?.modified,
        created_by: priceBook?.created_by,
        updated_by: priceBook?.updated_by,
      };
      await onSecondarySave?.(formData);
      onOpenChange(false);
    } finally {
      setIsLoading(false);
    }
  };

  const isEditing = !!priceBook?.id;
  const vendorId = form.watch('vendor_id');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Price Book' : 'Add Price Book'}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="vendor_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vendor *</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a vendor" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {vendors.filter(v => v.id).map((vendor) => (
                        <SelectItem key={vendor.id} value={vendor.id}>
                          {vendor.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="region_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Region</FormLabel>
                    <Select 
                      value={field.value || '__none__'} 
                      onValueChange={(value) => field.onChange(value === '__none__' ? null : value)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a region" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {regions.filter(r => r.id).map((region) => (
                          <SelectItem key={region.id} value={region.id}>
                            {region.display_value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="segment_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Segment</FormLabel>
                    <Select 
                      value={field.value || '__none__'} 
                      onValueChange={(value) => field.onChange(value === '__none__' ? null : value)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a segment" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {segments.filter(s => s.id).map((segment) => (
                          <SelectItem key={segment.id} value={segment.id}>
                            {segment.display_value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="effective_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Effective Date *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="expiry_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expiry Date</FormLabel>
                    <FormControl>
                      <Input 
                        type="date" 
                        {...field} 
                        value={field.value || ''} 
                        onChange={(e) => field.onChange(e.target.value || null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>


            <div className="flex justify-between gap-3 pt-4">
              <div>
                {isEditing && onDelete && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={isLoading || isDeleting}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Price Book
                  </Button>
                )}
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                {onSecondarySave && (
                  <Button 
                    type="button" 
                    variant="secondary"
                    disabled={isLoading || !vendorId}
                    onClick={handleSecondarySave}
                  >
                    {secondarySaveButtonText || 'Save & Close'}
                  </Button>
                )}
                <Button type="submit" disabled={isLoading || !vendorId}>
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    saveButtonText || 'Save'
                  )}
                </Button>
              </div>
            </div>

            {isEditing && (
              <RecordMetadata record={priceBook} />
            )}
          </form>
        </Form>
      </DialogContent>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Price Book</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this price book? ALL of the price book items will also be deleted. Should I delete this price book and its items?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async (e) => {
                e.preventDefault();
                if (priceBook?.id && onDelete) {
                  setIsDeleting(true);
                  try {
                    await onDelete(priceBook.id);
                    setDeleteDialogOpen(false);
                    onOpenChange(false);
                  } finally {
                    setIsDeleting(false);
                  }
                }
              }}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                'Yes, Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
